// Copyright ©️ 2026 eVolve MEP, LLC

import React, { type ReactNode, useEffect, useMemo } from 'react';

import type { PageTitle } from 'components/Mantine/Navigation/types';
import type { UseState } from 'helpers/helper-types';
import { useLocalStorage } from 'hooks/useLocalStorage';

type ChangeDataViewContextType = {
  viewsToShow: readonly SelectedView[];
  selectedView: SelectedView;
  setSelectedView: UseState<SelectedView>;
};
const ChangeDataViewContext = React.createContext<ChangeDataViewContextType | undefined>(undefined);

export const selectableViews = ['chart', 'list', 'table', 'kanban', 'calendar'] as const;
export type SelectedView = (typeof selectableViews)[number];

export const ChangeDataViewProvider = <T extends SelectedView>({
  children,
  name,
  viewsToShow,
  defaultView = viewsToShow[0],
}: {
  children: (selectedView: T) => ReactNode;
  name: PageTitle;
  viewsToShow: readonly [T, T, ...T[]];
  defaultView?: T;
}) => {
  const [selectedView, setSelectedView] = useLocalStorage(`DATA_VIEW_${name}`, defaultView);

  useEffect(() => {
    if (!viewsToShow.includes(selectedView as T)) {
      setSelectedView(defaultView);
    }
  }, [defaultView, selectedView, setSelectedView, viewsToShow]);

  const value = useMemo(
    () => ({
      selectedView,
      setSelectedView,
      viewsToShow,
    }),
    [selectedView, setSelectedView, viewsToShow],
  );

  return <ChangeDataViewContext value={value}>{children(selectedView as T)}</ChangeDataViewContext>;
};

// Do not use `useGeneralContext` as we need to check if the context is undefined downstream.
// If it is, we are not inside a ChangeDataViewProvider, so no need to render a `ChangeDataView`
export const useChangeDataView = () => React.useContext(ChangeDataViewContext);
